/**
 * Shared reCAPTCHA vocabulary.
 *
 * Lives here (and not in `@/app/api/utils/*`) because both the client hooks
 * that mint tokens and the server policy that asserts them need it, and client
 * code must never import from the api utils — those pull in `@/settings/private`.
 */

/**
 * The only two error codes this gate ever returns. Deliberately coarse: the
 * internal reason (low score, wrong action, invalid token…) stays server-side
 * so a bot can't learn why it was blocked. Both are also the reference codes
 * shown to the user for support.
 */
/** Verification ran and rejected the request. */
export const RECAPTCHA_FAILED_CODE = 40301
/** Verification could not run: no token reached us, or Google was unreachable. */
export const RECAPTCHA_UNAVAILABLE_CODE = 40303

export const RECAPTCHA_ERROR_CODES: readonly number[] = [
  RECAPTCHA_FAILED_CODE,
  RECAPTCHA_UNAVAILABLE_CODE,
]

/**
 * Action names, which bind a token to the flow it was minted for.
 *
 * Google silently strips any character outside `[A-Za-z0-9_/]`, so keep these
 * to lowercase words and underscores.
 *
 * `password_recover` has no server-side rule: password recovery is not a
 * governed operation. The client mints it anyway because it did so before this
 * ticket, and dropping it would be an unrelated behavior change.
 */
export type RecaptchaAction =
  | 'login'
  | 'register'
  | 'sso'
  | 'password_recover'
  | 'purchase'
  | 'manage_order'
  | 'update_billing_info'

/**
 * The two enforcement tiers. Named rather than inlined because the tier is the
 * key into both `settings.recaptcha.enforce` and `settings.recaptcha.minScore`:
 * an untiered rule would land on the auth floor and reject real buyers.
 */
export const SCORE_TIERS = {
  auth: 'auth',
  commerce: 'commerce',
} as const

export type ScoreTier = typeof SCORE_TIERS[keyof typeof SCORE_TIERS]
